import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { StockLogService, IStockLogService, IStockLog, StockLogType, StockLogEntityType, StockLogRefType, IInvoice } from '@posx/core';

export const useStockLogStore = defineStore('stockLog', {
  state: () => ({
    service: null as IStockLogService | null,
  }),

  actions: {
    async getService(): Promise<IStockLogService> {
      // service singleton is method-only, skip deep proxy
      if (!this.service) this.service = markRaw(await ServicesBuilder.getInstance().getService<IStockLogService>(StockLogService));
      return this.service;
    },

    async create(log: Partial<IStockLog>) {
      const svc = await this.getService();
      return svc.addOne({ ...log, entity_type: StockLogEntityType.Item } as IStockLog);
    },

    async createBatch(logs: Partial<IStockLog>[]) {
      const svc = await this.getService();
      return Promise.all(logs.map(log => svc.addOne({ ...log, entity_type: StockLogEntityType.Item } as IStockLog)));
    },

    async getByItem(entityUid: string) {
      const svc = await this.getService();
      // indexed read of one item's logs instead of getMany's full-table scan; keep getMany's soft-delete exclusion
      return svc.table.where('entity_uid').equals(entityUid).filter(l => !l.deleted_at).toArray();
    },

    async recordSalesLogs(invoice: IInvoice, employeeUid?: string) {
      const validLines = invoice.lines?.filter(l => l.item_uid && l.quantity > 0 && l.item) ?? [];
      if (!validLines.length) return;
      // merge duplicate item_uid lines; use first line's stock snapshot as qty_before
      const grouped = new Map<string, { stock: number; qty: number }>();
      for (const line of validLines) {
        // by_weight: quantity is in kg, stock is in grams - convert to grams
        const qty = line.item.type === 'by_weight' ? line.quantity * 1000 : line.quantity;
        const entry = grouped.get(line.item_uid);
        if (entry) entry.qty += qty;
        else grouped.set(line.item_uid, { stock: line.item.stock, qty });
      }
      const logs: Partial<IStockLog>[] = Array.from(grouped.entries()).map(([uid, { stock, qty }]) => ({
        entity_uid: uid,
        type: StockLogType.Out,
        qty: -qty,
        qty_before: stock,
        qty_after: stock - qty,
        ref_type: StockLogRefType.Invoice,
        ref_uid: invoice.uid,
        created_by: employeeUid,
      }));
      await this.createBatch(logs);
    },
  }
});

export { StockLogType, StockLogRefType, StockLogEntityType };
